'use client';

import { useEffect, useRef, useState } from 'react';

let activePreviewVideo = null;
const MAX_CONCURRENT_VIDEO_WARMUPS = 3;
let activeVideoWarmups = 0;
const videoWarmupQueue = [];

function releaseVideoWarmup(request) {
  if (!request?.active) return;
  request.active = false;
  activeVideoWarmups = Math.max(0, activeVideoWarmups - 1);
  while (videoWarmupQueue.length > 0 && activeVideoWarmups < MAX_CONCURRENT_VIDEO_WARMUPS) {
    const next = videoWarmupQueue.shift();
    if (!next || next.cancelled) continue;
    next.active = true;
    activeVideoWarmups += 1;
    next.start(() => releaseVideoWarmup(next));
  }
}

function queueVideoWarmup(start) {
  const request = { active: false, cancelled: false, start };
  videoWarmupQueue.push(request);
  if (activeVideoWarmups < MAX_CONCURRENT_VIDEO_WARMUPS) {
    const next = videoWarmupQueue.shift();
    if (next && !next.cancelled) {
      next.active = true;
      activeVideoWarmups += 1;
      next.start(() => releaseVideoWarmup(next));
    }
  }
  return () => {
    request.cancelled = true;
    releaseVideoWarmup(request);
  };
}

/**
 * Shared media surface extracted from the mature Gallery/Drafts cards.
 *
 * Gallery contributed the IntersectionObserver/save-data/reduced-motion rules;
 * Drafts contributed an explicit loading layer and loaded-state transition.
 * Published Feed cards stay paused until hover, so only the actively hovered
 * video plays. Visible cards only warm metadata; hover intent promotes the
 * active card to full preload. Posterless videos preload enough data to paint
 * their first frame immediately; showing the work takes priority over saving a
 * small amount of transfer on the first viewport.
 */
export default function MediaGallerySurface({
  src,
  poster = null,
  alt = '',
  isVideo = false,
  priority = false,
  className = '',
  onClick,
  loop = true,
  muted = true,
  playsInline = true,
}) {
  const mediaRef = useRef(null);
  const warmupReleaseRef = useRef(null);
  const [isVisible, setIsVisible] = useState(isVideo ? false : priority);
  const [isLoaded, setIsLoaded] = useState(Boolean(poster));
  const [hasWarmupSlot, setHasWarmupSlot] = useState(false);
  const [didWarmup, setDidWarmup] = useState(Boolean(poster));
  const [isPlaybackIntent, setIsPlaybackIntent] = useState(false);
  const [allowMotion, setAllowMotion] = useState(true);
  const [allowVideoLoad, setAllowVideoLoad] = useState(true);

  useEffect(() => {
    setIsLoaded(Boolean(poster));
    setDidWarmup(Boolean(poster));
    setHasWarmupSlot(false);
    setIsPlaybackIntent(false);
    warmupReleaseRef.current?.();
    warmupReleaseRef.current = null;
  }, [src, poster]);

  useEffect(() => {
    const node = mediaRef.current;
    if (!node || (priority && !isVideo)) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '500px' });

    observer.observe(node);
    return () => observer.disconnect();
  }, [isVideo, priority]);

  useEffect(() => {
    if (!isVideo || poster || !isVisible || didWarmup || isPlaybackIntent || !allowVideoLoad) return undefined;
    const cancel = queueVideoWarmup((release) => {
      warmupReleaseRef.current = release;
      setHasWarmupSlot(true);
    });
    return () => {
      cancel();
      if (!didWarmup) setHasWarmupSlot(false);
      if (warmupReleaseRef.current) warmupReleaseRef.current = null;
    };
  }, [allowVideoLoad, didWarmup, isPlaybackIntent, isVideo, isVisible, poster]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const connection = navigator?.connection;
    const update = () => {
      const saveData = Boolean(connection?.saveData);
      setAllowMotion(!motionQuery.matches && !saveData);
      setAllowVideoLoad(!saveData);
    };
    update();
    motionQuery.addEventListener('change', update);
    connection?.addEventListener?.('change', update);
    return () => {
      motionQuery.removeEventListener('change', update);
      connection?.removeEventListener?.('change', update);
    };
  }, []);

  useEffect(() => {
    if (!isVideo || !isPlaybackIntent || !allowMotion || !mediaRef.current) return undefined;
    const node = mediaRef.current;
    if (activePreviewVideo && activePreviewVideo !== node) activePreviewVideo.pause();
    activePreviewVideo = node;
    node.preload = 'auto';
    if (node.readyState === 0) node.load();
    node.play().catch(() => {});
    return () => {
      node.pause();
      if (activePreviewVideo === node) activePreviewVideo = null;
    };
  }, [allowMotion, isPlaybackIntent, isVideo, src]);

  useEffect(() => {
    const node = mediaRef.current;
    if (!isVideo || !hasWarmupSlot || !node) return;
    node.preload = 'auto';
    if (node.readyState === 0) node.load();
  }, [hasWarmupSlot, isVideo, src]);

  const handlePointerEnter = () => {
    if (!isVideo || !allowMotion) return;
    setIsPlaybackIntent(true);
  };

  const handlePointerLeave = () => {
    if (!isVideo || !mediaRef.current) return;
    const node = mediaRef.current;
    setIsPlaybackIntent(false);
    node.pause();
    if (activePreviewVideo === node) activePreviewVideo = null;
  };

  const finishWarmup = () => {
    setIsLoaded(true);
    setDidWarmup(true);
    setHasWarmupSlot(false);
    warmupReleaseRef.current?.();
    warmupReleaseRef.current = null;
  };

  const shouldAttachVideo = isPlaybackIntent
    || (poster ? (isVisible && allowMotion) : (didWarmup || hasWarmupSlot));
  const sharedClassName = `${className} transition duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`;

  return (
    <div
      className="relative h-full w-full overflow-hidden bg-[#1f1f23]"
      data-media-gallery-surface={isVideo ? 'video' : 'image'}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      {!isLoaded ? (
        <div
          className="absolute inset-0 animate-pulse bg-gradient-to-b from-white/[0.07] to-white/[0.025] motion-reduce:animate-none"
          aria-hidden="true"
        />
      ) : null}
      {isVideo ? (
        <video
          ref={mediaRef}
          src={shouldAttachVideo ? src : undefined}
          data-src={src}
          poster={poster || undefined}
          preload={shouldAttachVideo ? (isPlaybackIntent || !poster ? 'auto' : 'metadata') : 'none'}
          className={sharedClassName}
          loop={loop}
          muted={muted}
          playsInline={playsInline}
          onClick={onClick}
          onLoadedData={finishWarmup}
          onError={finishWarmup}
          aria-label={alt || 'Video preview'}
        />
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={mediaRef}
          src={isVisible ? src : undefined}
          data-src={src}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          className={sharedClassName}
          onClick={onClick}
          onLoad={() => setIsLoaded(true)}
          onError={() => setIsLoaded(true)}
        />
      )}
    </div>
  );
}
